import styled, { css } from "styled-components";

import { INPUT_BUTTON_SIZE, SVG_ICON_MARGIN, SVG_ICON_SIZE } from "uikit/constants";
import { elementSpacingMixin } from "uikit/styles";

import {
  BUTTON_BORDER_RADIUS,
  BUTTON_FIXED_WIDTH_MAX,
  BUTTON_FONT_SIZE,
  BUTTON_LINE_HEIGHT,
  BUTTON_PADDING,
} from "./constants";
import { IButtonProps } from "./types";
export const resetButtonStyles = css`
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  text-align: inherit;
  line-height: inherit;
  cursor: pointer;

  &:focus {
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const textMixin = css<IButtonProps>`
  font-family: ${({ theme }) => theme.fontFamily.default};
  font-weight: ${({ theme }) => theme.fontWeight.semiBold};
  font-size: ${({ $size }) => BUTTON_FONT_SIZE[$size || "medium"]};
  line-height: ${({ $size }) => BUTTON_LINE_HEIGHT[$size || "medium"]};
`;

export const buttonBaseMixin = css<IButtonProps>`
  ${resetButtonStyles};
  ${elementSpacingMixin};
  ${textMixin};
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border-radius: ${BUTTON_BORDER_RADIUS};
  min-width: max-content;
  transition:
    background-color 0.3s,
    color 0.3s,
    border-color 0.3s;
`;

export const sizeButtonMixin = css<IButtonProps>`
  height: ${({ $size }) => INPUT_BUTTON_SIZE[$size || "medium"]};
  padding: ${({ $size }) => BUTTON_PADDING[$size || "medium"]};
  width: auto;

  svg {
    width: ${({ $size }) => SVG_ICON_SIZE[$size || "medium"]};
    height: ${({ $size }) => SVG_ICON_SIZE[$size || "medium"]};
  }

  &:has(svg) {
    gap: 0 ${({ $size }) => SVG_ICON_MARGIN[$size || "medium"]};
  }

  ${({ $isMaximized }) =>
    $isMaximized &&
    css`
      max-width: ${BUTTON_FIXED_WIDTH_MAX} !important;
      width: 100% !important;
    `};

  ${({ $isParentWidth }) =>
    $isParentWidth &&
    css`
      max-width: 100% !important;
      width: 100% !important;
    `};
`;

const primaryButtonMixin = css`
  background-color: ${({ theme }) => theme.colors.colorsNew.primary["500_main"]};
  color: ${({ theme }) => theme.colors.colorsNew.base.white};

  &:hover {
    background-color: ${({ theme }) => theme.colors.colorsNew.primary[400]};
    color: ${({ theme }) => theme.colors.colorsNew.base.white};
  }

  &:active {
    background-color: ${({ theme }) => theme.colors.colorsNew.primary[600]};
    color: ${({ theme }) => theme.colors.colorsNew.base.white};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.colorsNew.gray[200]};
    color: ${({ theme }) => theme.colors.colorsNew.gray[400]};
  }
`;

const ghostButtonMixin = css`
  background-color: ${({ theme }) => theme.colors.colorsNew.base.white};
  border-style: solid;
  border-width: 1px;
  border-color: ${({ theme }) => theme.colors.colorsNew.primary["500_main"]};
  color: ${({ theme }) => theme.colors.colorsNew.primary["500_main"]};

  &:hover {
    border-color: ${({ theme }) => theme.colors.colorsNew.primary[400]};
    color: ${({ theme }) => theme.colors.colorsNew.primary[400]};
  }

  &:active {
    border-color: ${({ theme }) => theme.colors.colorsNew.primary[600]};
    color: ${({ theme }) => theme.colors.colorsNew.primary[600]};
  }

  &:disabled {
    border-color: ${({ theme }) => theme.colors.colorsNew.gray[400]};
    color: ${({ theme }) => theme.colors.colorsNew.gray[400]};
  }
`;

const secondaryButtonMixin = css`
  padding: 0;
  height: auto;
  background-color: transparent;
  color: ${({ theme }) => theme.colors.colorsNew.blue.blue};

  &:hover {
    background-color: transparent;
    color: ${({ theme }) => theme.colors.colorsNew.blue.blue_hover};
  }

  &:active {
    background-color: transparent;
    color: ${({ theme }) => theme.colors.colorsNew.blue.blue_hover};
  }

  &:disabled {
    background-color: transparent;
    color: ${({ theme }) => theme.colors.colorsNew.gray[400]};
  }
`;

const errorButtonMixin = css`
  background-color: ${({ theme }) => theme.colors.colorsNew.error[100]};
  color: ${({ theme }) => theme.colors.colorsNew.error["500_main"]};

  &:hover {
    background-color: ${({ theme }) => theme.colors.colorsNew.error[100]};
    color: ${({ theme }) => theme.colors.colorsNew.error[600]};
  }

  &:active {
    background-color: ${({ theme }) => theme.colors.colorsNew.error[100]};
    color: ${({ theme }) => theme.colors.colorsNew.error[600]};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.colorsNew.gray[200]};
    color: ${({ theme }) => theme.colors.colorsNew.gray[400]};
  }
`;

export const BaseButton = styled.button<IButtonProps>`
  ${buttonBaseMixin};
  ${sizeButtonMixin};

  ${({ $variant }) =>
    ($variant === "primary" && primaryButtonMixin) ||
    ($variant === "ghost" && ghostButtonMixin) ||
    ($variant === "secondary" && secondaryButtonMixin) ||
    ($variant === "error" && errorButtonMixin) ||
    primaryButtonMixin};
`;
